import { useEffect, useState, useMemo } from 'react';
import { Filter, Search } from 'lucide-react';
import {
  DataTable, EmptyState, FilterBuilder, Pagination, StatusPill,
  type ColumnDef, type FilterCondition, type FilterField,
} from '@vnetwork/ui/data';
import { Alert } from '@vnetwork/ui/feedback';
import { Badge, Button, Input, Popover, PopoverContent, PopoverTrigger } from '@vnetwork/ui/primitives';
import { Toolbar } from '@vnetwork/ui/layout';
import { formatDateTime } from '@/lib/format';
import { useAffiliateClicks, type AffiliateClick } from '@/api/affiliate';
import { paginationView } from '@/pages/customers/pagination-summary';

const PAGE_SIZE = 20;

interface ClicksTabProps {
  affiliateId: number;
}

const CONVERTED_OPTIONS = [
  { value: 'true', label: 'Converted' },
  { value: 'false', label: 'Not converted' },
];

const FILTER_FIELDS: FilterField[] = [
  { value: 'converted', label: 'Converted', input: 'select', options: CONVERTED_OPTIONS },
];

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

/** Shared column definitions for the clicks table. */
export function buildClickColumns(): ColumnDef<AffiliateClick, unknown>[] {
  return [
    {
      id: 'click_id',
      header: 'Click ID',
      cell: ({ row }) => row.original.click_id,
    },
    {
      id: 'visitor_id',
      header: 'Visitor ID',
      cell: ({ row }) => (
        <code style={{ fontSize: 'var(--vn-font-size-xs)' }}>
          {row.original.visitor_id || '—'}
        </code>
      ),
    },
    {
      id: 'ip_address',
      header: 'IP Address',
      cell: ({ row }) => row.original.ip_address || '—',
    },
    {
      id: 'referer_url',
      header: 'Referer',
      cell: ({ row }) => (
        <span
          style={{
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: 220,
          }}
          title={row.original.referer_url || undefined}
        >
          {row.original.referer_url || '—'}
        </span>
      ),
    },
    {
      id: 'utm_source',
      header: 'UTM Source',
      cell: ({ row }) => row.original.utm_source || '—',
    },
    {
      id: 'promo_code',
      header: 'Promo Code',
      cell: ({ row }) => <code>{row.original.promo_code || '—'}</code>,
    },
    {
      id: 'converted',
      header: 'Converted',
      cell: ({ row }) =>
        row.original.converted ? (
          <StatusPill tone="success">Yes</StatusPill>
        ) : (
          <StatusPill tone="neutral">No</StatusPill>
        ),
    },
    {
      id: 'clicked_at',
      header: 'Clicked At',
      cell: ({ row }) => formatDateTime(row.original.clicked_at),
    },
  ];
}

/**
 * Clicks tab — server-paginated click log with converted filter bar.
 */
export function ClicksTab({ affiliateId }: ClicksTabProps) {
  const [page, setPage] = useState(1);
  const [rawQuery, setRawQuery] = useState('');
  const query = useDebouncedValue(rawQuery, 400);
  const [filterOpen, setFilterOpen] = useState(false);
  const [conditions, setConditions] = useState<FilterCondition[]>([]);

  const convertedFilter = conditions.find((c) => c.field === 'converted')?.value;
  const convertedParam =
    convertedFilter === 'true' ? true : convertedFilter === 'false' ? false : undefined;

  // `query` is client-side only — the clicks endpoint has no search param.
  // We fetch the page and let the DataTable handle the visual; real search
  // would need a backend param once the endpoint supports it.
  const { data, isLoading, isError, refetch } = useAffiliateClicks(affiliateId, {
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
    ...(convertedParam !== undefined ? { converted: convertedParam } : {}),
  });

  const allRows = data?.results ?? [];
  // Apply the query client-side against the loaded page (visitor_id, ip_address, promo_code)
  const rows = query
    ? allRows.filter((r) => {
        const q = query.toLowerCase();
        return (
          r.visitor_id?.toLowerCase().includes(q) ||
          r.ip_address?.toLowerCase().includes(q) ||
          r.promo_code?.toLowerCase().includes(q) ||
          r.utm_source?.toLowerCase().includes(q)
        );
      })
    : allRows;

  const total = data?.pagination?.total ?? allRows.length;
  const pageCount = data?.pagination?.total_pages ?? 1;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const columns = useMemo(() => buildClickColumns(), []);

  const appliedCount = new Set(conditions.map((c) => c.field)).size;

  const applyFilters = (next: FilterCondition[]) => {
    setConditions(next.filter((c) => c.value !== ''));
    setPage(1);
    setFilterOpen(false);
  };

  if (isError) {
    return (
      <Alert
        tone="danger"
        title="Failed to load clicks"
        actions={
          <Button size="sm" variant="ghost" onClick={() => refetch()}>
            Retry
          </Button>
        }
      />
    );
  }

  const view = paginationView(page, PAGE_SIZE, allRows.length, total, pageCount);

  return (
    <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
      <Toolbar
        style={{ marginBottom: 0 }}
        start={
          <Input
            value={rawQuery}
            onChange={(e) => {
              setRawQuery(e.target.value);
              setPage(1);
            }}
            placeholder="Filter by visitor, IP, UTM…"
            startSlot={<Search size={15} />}
            style={{ maxWidth: 320 }}
          />
        }
        end={
          <Popover open={filterOpen} onOpenChange={setFilterOpen}>
            <PopoverTrigger asChild>
              <Button variant="secondary" startIcon={<Filter size={15} />}>
                Filters{appliedCount ? <Badge tone="primary">{appliedCount}</Badge> : null}
              </Button>
            </PopoverTrigger>
            <PopoverContent align="start" style={{ padding: 0 }}>
              <FilterBuilder
                fields={FILTER_FIELDS}
                defaultValue={conditions.length ? conditions : undefined}
                onApply={applyFilters}
                onClose={() => setFilterOpen(false)}
              />
            </PopoverContent>
          </Popover>
        }
      />

      <DataTable
        columns={columns}
        data={rows}
        enableSorting={false}
        getRowId={(r) => String(r.click_id)}
        emptyState={
          <EmptyState
            title="No clicks yet"
            description="Clicks through this affiliate's referral link will appear here."
          />
        }
        footer={
          isLoading ? null : (
            <Pagination
              page={page}
              pageCount={view.pageCount}
              onPageChange={setPage}
              summary={view.summary}
            />
          )
        }
      />
    </div>
  );
}
